import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Guid, isNotEmptyArray, isNotNil, Nillable } from '@web/utils';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, switchMap } from 'rxjs';
import {
  DetailsViewComponent,
  NoDataComponent,
  SpinnerFilledComponent,
} from '@web/shared/ui';
import { AccordionModule } from 'primeng/accordion';
import { GalleriaModule } from 'primeng/galleria';
import { WarehouseRestockDataSourceService } from '../../services/warehouse-restock-data-source.service';
import { WarehouseRestockDetails } from '../../models/warehouse-restock-details';
import { WarehouseRestockDetailsAttributesComponent } from '../../components/warehouse-restock-details-attributes/warehouse-restock-details-attributes.component';
import { WarehouseRestockDetailsProductAttributesComponent } from '../../components/warehouse-restock-details-product-attributes/warehouse-restock-details-product-attributes.component';
import { WarehouseRestockProduct } from '../../models/warehouse-restock-product';
import { MultimediaGalleriaThumbnailsComponent } from '@web/shared/module/multimedia';
import { WarehouseMultimediaDataSourceService } from '../../services/warehouse-multimedia-data-source.service';

@Component({
  selector: 'app-warehouse-restock-details',
  imports: [
    AccordionModule,
    CommonModule,
    GalleriaModule,
    SpinnerFilledComponent,
    WarehouseRestockDetailsAttributesComponent,
    WarehouseRestockDetailsProductAttributesComponent,
    DetailsViewComponent,
    NoDataComponent,
    MultimediaGalleriaThumbnailsComponent,
  ],
  templateUrl: './warehouse-restock-details.component.html',
  styleUrl: './warehouse-restock-details.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WarehouseRestockDetailsComponent {
  /* prettier-ignore */
  private readonly warehouseRestockDataSource = inject(WarehouseRestockDataSourceService);
  /* prettier-ignore */
  private readonly warehouseMultimediaDataSource = inject(WarehouseMultimediaDataSourceService);

  public readonly id = input.required<Guid>();
  public readonly thumbnailsVisibility = input(false);

  protected readonly id$ = toObservable(this.id);
  protected readonly details$: Observable<WarehouseRestockDetails>;

  public shouldShowProductAccordions(
    products: Nillable<WarehouseRestockProduct[]>,
  ): products is WarehouseRestockProduct[] {
    return isNotNil(products) && isNotEmptyArray(products);
  }

  public constructor() {
    this.details$ = this.detailsSource();
  }

  /* prettier-ignore */
  protected readonly fetchMultimediaDetailsFn = (id: Guid) => this.warehouseMultimediaDataSource.fetchDetailsById(id, true);

  private detailsSource(): Observable<WarehouseRestockDetails> {
    return this.id$.pipe(
      switchMap((id) => this.warehouseRestockDataSource.fetchDetailsById(id)),
    );
  }
}
